import React, { useState } from 'react';
import { previewCostBasisChange, applyCostBasisChange } from '../../composables/fetchAPI';
import toast from 'react-hot-toast';

interface CostBasisModalProps {
  isOpen: boolean;
  onClose: () => void;
  portfolioId: string;
  currentConfig: any;
  onSave: () => void;
}

const CostBasisModal: React.FC<CostBasisModalProps> = ({ isOpen, onClose, portfolioId, currentConfig, onSave }) => {
  const [config, setConfig] = useState(currentConfig || { preset: 'WEBULL', method: 'WAC', compressionStrategy: 'NONE' });
  const [isCustom, setIsCustom] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const methods = ['WAC', 'FIFO', 'COMPRESSED_FIFO', 'TAX_MINIMIZATION'];
  const compressions = ['NONE', 'DAILY', 'SAME_DAY_AND_PRICE'];

  // --- LOGIC RULES ตามเอกสาร ---
  const isCompressed = config.method === 'COMPRESSED_FIFO';
  const showCompressionSelector = isCompressed; // ตามเอกสาร: COMPRESSED_FIFO ต้องมี compression [cite: 360]

  const handlePresetChange = (preset: string) => {
    if (preset === 'CUSTOM') {
      setIsCustom(true);
      setConfig({ preset: 'CUSTOM', method: 'WAC', compressionStrategy: 'NONE' });
    } else {
      setIsCustom(false);
      let newConfig = { preset: preset, method: 'WAC', compressionStrategy: 'NONE' };

      // ตั้งค่าตาม Preset เฉพาะ
      if (preset === 'DIME') {
        newConfig = { preset: 'DIME', method: 'COMPRESSED_FIFO', compressionStrategy: 'DAILY' };
      } else if (preset === 'M1') {
        newConfig = { preset: 'M1', method: 'TAX_MINIMIZATION', compressionStrategy: 'NONE' };
      }

      setConfig(newConfig);
    }
  };

  const handleMethodChange = (newMethod: string) => {
    let newCompression = config.compressionStrategy;

    // ตามกฎเอกสาร: WAC/FIFO/TAX_MINIMIZATION ควรใช้ NONE 
    if (newMethod === 'WAC' || newMethod === 'FIFO' || newMethod === 'TAX_MINIMIZATION') {
      newCompression = 'NONE';
    } else if (newMethod === 'COMPRESSED_FIFO' && newCompression === 'NONE') {
      // COMPRESSED_FIFO ต้องมีการเลือก Compression เสมอ 
      newCompression = 'DAILY';
    }

    setConfig({ ...config, method: newMethod, compressionStrategy: newCompression });
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      const res = await previewCostBasisChange(portfolioId, config, 'Preview cost basis change');
      setPreviewData(res?.data || res);
    } catch (err: any) {
      toast.error('Preview ล้มเหลว');
    }
    setLoading(false);
  };

  const handleApply = async () => {
    if (!previewData?.confirmToken) return;
    setLoading(true);
    try {
      await applyCostBasisChange(portfolioId, config, previewData.confirmToken, 'Apply');
      toast.success('เปลี่ยน Cost Basis สำเร็จ!');
      onSave();
      onClose();
    } catch (err: any) {
      // แสดงข้อความจาก API ตามที่ระบุ 
      const errorMsg = err?.response?.data?.th_message || err?.response?.data?.message || 'เกิดข้อผิดพลาด';
      toast.error(errorMsg);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div className="bg-[#18181b] border border-gray-700 p-6 rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <h2 className="text-yellow-400 text-xl font-bold mb-6">ตั้งค่า Cost Basis</h2>

        <div className="space-y-5">
          <div>
            <label className="text-gray-400 text-xs font-bold uppercase">เลือกรูปแบบ (Preset)</label>
            <select value={isCustom ? 'CUSTOM' : config.preset} onChange={(e) => handlePresetChange(e.target.value)}
              className="w-full bg-gray-900 text-white p-3 rounded-lg mt-1 border border-gray-700">
              <option value="WEBULL">WEBULL (WAC)</option>
              <option value="DIME">DIME (Compressed FIFO)</option>
              <option value="M1">M1 FINANCE (Tax Minimization)</option>
              <option value="CUSTOM">Custom...</option>
            </select>
          </div>

          {isCustom && (
            <div className="p-4 bg-gray-900/50 rounded-xl border border-gray-700 space-y-4">
              <div>
                <label className="text-gray-400 text-xs font-bold">Method</label>
                <select value={config.method} onChange={(e) => handleMethodChange(e.target.value)}
                  className="w-full bg-gray-800 text-white p-2 rounded mt-1 border border-gray-600">
                  {methods.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              {/* Compression Selector เฉพาะเมื่อเป็น COMPRESSED_FIFO [cite: 449] */}
              {showCompressionSelector && (
                <div>
                  <label className="text-gray-400 text-xs font-bold">Compression Strategy</label>
                  <select value={config.compressionStrategy} onChange={(e) => setConfig({ ...config, compressionStrategy: e.target.value })}
                    className="w-full bg-gray-800 text-white p-2 rounded mt-1 border border-gray-600">
                    <option value="DAILY">DAILY (รายวัน)</option>
                    <option value="SAME_DAY_AND_PRICE">SAME_DAY_AND_PRICE (วัน+ราคาเดียวกัน)</option>
                  </select>
                </div>
              )}
            </div>
          )}

          <button onClick={handlePreview} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 text-white py-3 rounded-lg font-bold transition">
            {loading ? 'กำลังคำนวณ...' : 'ตรวจสอบผลกระทบ (Preview)'}
          </button>
        </div>

        {previewData?.impact && (
          <div className="mt-6 p-4 bg-gray-900 rounded-xl border border-yellow-900/50">
            <h3 className="text-yellow-400 font-bold mb-2 text-sm">⚠️ คำเตือน:</h3>
            <ul className="text-xs text-gray-300 space-y-1 mb-4">
              {previewData.impact.importantWarnings?.map((w: any, i: number) => <li key={i}>• {w.message}</li>)}
            </ul>
            <button onClick={handleApply} className="w-full bg-yellow-400 hover:bg-yellow-300 text-black py-3 rounded-lg font-bold">
              ยืนยันการเปลี่ยนแปลง
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostBasisModal;